import { useCallback, useEffect, useRef, useState } from 'react'

interface UseKeyPressesParams {
  filter?: (e: KeyboardEvent) => boolean
  onPress?: (e: KeyboardEvent) => void
}

function useKeyPresses(
  params?: UseKeyPressesParams | undefined,
): KeyboardEvent | null {
  const [keyPress, setKeyPress] = useState<KeyboardEvent | null>(null)

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      setKeyPress(e)
      if (params?.filter && !params.filter(e)) {
        return
      }

      params?.onPress?.(e)
    }

    const keyUpHandler = () => {
      setKeyPress(null)
    }

    window.addEventListener('keydown', keyDownHandler)
    window.addEventListener('keyup', keyUpHandler)

    return () => {
      window.removeEventListener('keydown', keyDownHandler)
      window.removeEventListener('keyup', keyUpHandler)
    }
  }, [params?.onPress])

  return keyPress
}

export interface KeyPressConfig {
  code: string
  altKey?: boolean
  ctrlKey?: boolean
  metaKey?: boolean
  shiftKey?: boolean
}

export interface SequenceConfig {
  keys: KeyPressConfig[]
  callback: () => void
}

type SequenceState = {
  currentIndex: number
}

/**
 * Hook to manage MULTIPLE key sequences with a SINGLE keydown listener.
 * Uses array index internally to track state for each sequence.
 * Uses event.modifierKey properties directly for modifier checks.
 *
 * @param {SequenceConfig[]} sequences - Array of sequence definitions (order should be stable).
 * @param {boolean} [enableDebugLogging=false] - Enable console logs.
 */
function useKeySequences(
  sequences: SequenceConfig[],
  enableDebugLogging: boolean = false,
): void {
  const sequenceStatesRef = useRef<SequenceState[]>([])
  const sequencesRef = useRef(sequences)

  const log = useCallback(
    (...args: any[]) => {
      if (enableDebugLogging) {
        console.log(
          '%c[useKeySequences]',
          'color: teal; font-weight: bold;',
          ...args,
        )
      }
    },
    [enableDebugLogging],
  )

  useEffect(() => {
    log('Sequences definition updated or hook mounted. Resetting states.')
    sequencesRef.current = sequences // Store the latest sequences
    sequenceStatesRef.current = sequences.map(() => ({ currentIndex: 0 }))
    log(`Initialized ${sequenceStatesRef.current.length} sequence states.`)
  }, [sequences, log])

  // Checks if the event matches a step config, using event properties directly.
  const checkMatch = useCallback(
    (event: KeyboardEvent, config: KeyPressConfig): boolean => {
      if (event.code !== config.code) {
        return false
      }

      const altMatch = event.altKey === (config.altKey ?? false)
      const ctrlMatch = event.ctrlKey === (config.ctrlKey ?? false)
      const metaMatch = event.metaKey === (config.metaKey ?? false)
      const shiftMatch = event.shiftKey === (config.shiftKey ?? false)

      if (!altMatch || !ctrlMatch || !metaMatch || !shiftMatch) {
        log(
          `Code '${config.code}' matched, but modifiers mismatched: ` +
            `Event(A:${event.altKey},C:${event.ctrlKey},M:${event.metaKey},S:${event.shiftKey}) vs ` +
            `Config(A:${config.altKey ?? false},C:${config.ctrlKey ?? false},M:${config.metaKey ?? false},S:${config.shiftKey ?? false})`,
        )
        return false
      }
      return true
    },
    [log],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const code = event.code
      if (enableDebugLogging) {
        log(
          `Key Down: code=${code}, key=${event.key} | Event Mods: A:${event.altKey} C:${event.ctrlKey} M:${event.metaKey} S:${event.shiftKey}`,
        )
      }

      const target = event.target as HTMLElement
      const tagName = target?.tagName?.toUpperCase()
      if (
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'SELECT'
      ) {
        log(`Ignoring event in focused ${tagName}.`)
        return
      }

      let preventDefaultCalled = false

      // Iterate through sequences using index
      sequencesRef.current.forEach((sequence, index) => {
        const sequenceKeys = sequence.keys
        const state = sequenceStatesRef.current[index]

        // Skip if state or keys are invalid/missing (shouldn't happen with proper init)
        if (!state || !sequenceKeys || sequenceKeys.length === 0) {
          log(`Skipping sequence index ${index}: invalid state or empty keys.`)
          return // continue to next iteration
        }

        const currentExpectedIndex = state.currentIndex

        if (currentExpectedIndex >= sequenceKeys.length) {
          return
        }

        const requiredPressConfig = sequenceKeys[currentExpectedIndex]

        log(
          `Checking sequence index ${index}, step ${currentExpectedIndex}: Needs ${JSON.stringify(requiredPressConfig)}`,
        )

        // Check if the current event matches the *expected* step
        if (checkMatch(event, requiredPressConfig)) {
          log(
            `>>> Matched step ${currentExpectedIndex} for sequence index ${index}`,
          )

          if (
            !preventDefaultCalled &&
            (requiredPressConfig.altKey ||
              requiredPressConfig.ctrlKey ||
              requiredPressConfig.metaKey)
          ) {
            log(
              `Preventing default action for ${event.code} due to match in sequence index ${index} requiring modifiers.`,
            )
            event.preventDefault()
            preventDefaultCalled = true
          }

          state.currentIndex += 1

          // Check for completion
          if (state.currentIndex === sequenceKeys.length) {
            log(`>>> Sequence COMPLETE: index ${index} <<<`)
            try {
              sequence.callback()
            } catch (e) {
              console.error(`Error in callback for sequence index ${index}:`, e)
            }
            state.currentIndex = 0
          }
        } else {
          // Event did NOT match the expected step for this sequence
          if (currentExpectedIndex > 0) {
            // Check for immediate restart: does event match step 0?
            const firstStepConfig = sequenceKeys[0]

            if (checkMatch(event, firstStepConfig)) {
              log(
                `<< Seq index ${index}: Incorrect step ${currentExpectedIndex}, but matches step 0. Resetting to step 1. >>`,
              )
              state.currentIndex = 1

              // Handle completion if sequence has length 1
              if (state.currentIndex === sequenceKeys.length) {
                log(
                  `>>> Sequence COMPLETE (len 1 on immediate restart): index ${index} <<<`,
                )
                try {
                  sequence.callback()
                } catch (e) {
                  console.error(
                    `Error in callback for sequence index ${index}:`,
                    e,
                  )
                }
                state.currentIndex = 0
              }
            } else {
              // Didn't match current step, didn't match step 0. Reset fully.
              log(
                `<< Seq index ${index}: Event did not match step ${currentExpectedIndex}. Resetting sequence fully. >>`,
              )
              state.currentIndex = 0
            }
          }
        }
      })
    },
    [checkMatch, log, enableDebugLogging],
  )

  useEffect(() => {
    log('Attaching SINGLE simplified keydown listener (using index).')
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      log('Removing SINGLE simplified keydown listener (using index).')
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown, log])
}

export { useKeyPresses, useKeySequences }
